import { EVERYONE_SENTINEL } from "@shared/types";
import { genId } from "@/components/plan/planUtils";
export { EVERYONE_SENTINEL };
export function isEveryoneAssignment(a) {
    return a.displayName === EVERYONE_SENTINEL;
}
/** Stable identity key for deduplicating assignment rows. */
function assignmentKey(a) {
    return a.participantId ? `id:${a.participantId}` : `name:${a.displayName}`;
}
/** Does this assignment row represent the given person? */
function assignmentIsPerson(a, participantId, displayName) {
    if (participantId !== undefined && a.participantId === participantId)
        return true;
    if (a.participantId === undefined && a.displayName === displayName)
        return true;
    return false;
}
/** Drop duplicate rows (same participantId, or same displayName when id-less). */
export function dedupeAssignments(assignments) {
    const seen = new Set();
    const out = [];
    for (const a of assignments) {
        const key = assignmentKey(a);
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(a);
    }
    return out;
}
function dedupeStrings(values) {
    return Array.from(new Set(values));
}
/**
 * Derive the assignment type for an item, honoring an explicit `assignmentType`
 * when present and falling back to legacy shapes (sentinel row / legacy single
 * fields) otherwise.
 */
export function getAssignmentType(item) {
    if (item.assignmentType)
        return item.assignmentType;
    if (item.assignments && item.assignments.length > 0) {
        if (item.assignments.some(isEveryoneAssignment))
            return "all";
        return "specific";
    }
    if (item.assignedTo || item.assignedToParticipantId)
        return "specific";
    return "none";
}
/**
 * Number of distinct specific assignees on an item (the everyone sentinel is
 * never counted as a real person). For `all`/`none` items this reflects only
 * the rows that physically exist, so callers should branch on the assignment
 * type first when they care about coverage.
 */
export function specificAssigneeCount(item) {
    const assignments = item.assignments ?? migrateToAssignments(item);
    const real = dedupeAssignments(assignments.filter((a) => !isEveryoneAssignment(a)));
    return real.length;
}
/**
 * How many distinct assignees an item needs before it counts as fully
 * assigned. Defaults to 1 so plans created before `requiredAssigneesCount`
 * existed behave exactly as before. Guards against bad/negative values.
 */
export function requiredSlots(item) {
    const raw = item.requiredAssigneesCount;
    if (typeof raw !== "number" || !Number.isFinite(raw) || raw < 1)
        return 1;
    return Math.floor(raw);
}
/**
 * How many of an item's required assignment slots are currently filled.
 * `all` items fill every slot; `specific` items fill one slot per distinct
 * assignee (capped at the requirement); `none` items fill nothing. This is the
 * single source of truth for assignment-coverage math across the overview and
 * section breakdowns.
 */
export function filledSlots(item) {
    const type = getAssignmentType(item);
    if (type === "none")
        return 0;
    const required = requiredSlots(item);
    if (type === "all")
        return required;
    return Math.min(specificAssigneeCount(item), required);
}
/**
 * Planning status for an item, combining assignment coverage with completion.
 * Completion wins over assignment state: a checked item reads as `completed`
 * regardless of how many assignees it has.
 */
export function getAssignmentStatus(item) {
    if (item.checked)
        return "completed";
    const type = getAssignmentType(item);
    if (type === "none")
        return "unassigned";
    if (type === "all")
        return "assigned";
    // specific
    const count = specificAssigneeCount(item);
    if (count === 0)
        return "unassigned";
    return count >= requiredSlots(item) ? "assigned" : "partial";
}
/**
 * Parse a value into a positive integer, falling back when it is missing,
 * non-numeric, zero, or negative. Strings keep backward compatibility with the
 * free-text quantity model (e.g. `"4"`, or a legacy `"20 יח׳"` → 20).
 */
export function parsePositiveInt(value, fallback) {
    if (typeof value === "number") {
        return Number.isFinite(value) && value >= 1 ? Math.floor(value) : fallback;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed)
            return fallback;
        const n = parseInt(trimmed, 10);
        return Number.isFinite(n) && n >= 1 ? n : fallback;
    }
    return fallback;
}
/** Total units an item requires. Defaults to 1 for invalid/missing quantities. */
export function getItemRequiredQuantity(item) {
    return parsePositiveInt(item.quantity, 1);
}
/**
 * Units a single assignment contributes. Legacy assignments without an explicit
 * quantity count as 1 (and get an explicit quantity persisted on next edit).
 */
export function getAssignmentQuantity(a) {
    return parsePositiveInt(a.quantity, 1);
}
/**
 * Units currently covered for an item:
 * - `none`     — nothing is covered.
 * - `all`      — treated as fully covered (everyone brings it).
 * - `specific` — the sum of each distinct assignee's quantity.
 */
export function getItemCoveredQuantity(item) {
    const type = getAssignmentType(item);
    if (type === "none")
        return 0;
    if (type === "all")
        return getItemRequiredQuantity(item);
    const assignments = dedupeAssignments((item.assignments ?? migrateToAssignments(item)).filter((a) => !isEveryoneAssignment(a)));
    return assignments.reduce((sum, a) => sum + getAssignmentQuantity(a), 0);
}
/** Units still missing for an item (never negative). */
export function getItemMissingQuantity(item) {
    return Math.max(0, getItemRequiredQuantity(item) - getItemCoveredQuantity(item));
}
/** Coverage state derived purely from required vs covered quantities. */
export function getItemCoverageState(item) {
    const required = getItemRequiredQuantity(item);
    const covered = getItemCoveredQuantity(item);
    if (covered === 0)
        return "uncovered";
    if (covered > required)
        return "over";
    if (covered >= required)
        return "covered";
    return "partial";
}
/** Full coverage snapshot for an item. */
export function getItemCoverage(item) {
    const required = getItemRequiredQuantity(item);
    const covered = getItemCoveredQuantity(item);
    let state;
    if (covered === 0)
        state = "uncovered";
    else if (covered > required)
        state = "over";
    else if (covered >= required)
        state = "covered";
    else
        state = "partial";
    return { required, covered, missing: Math.max(0, required - covered), state };
}
/**
 * Is the item's required quantity not fully covered? True for uncovered and
 * partially covered items; false once covered (or over-covered). Drives the
 * "חסר כיסוי" category filter.
 */
export function isItemMissingCoverage(item) {
    // "Buy there" items are planned to be handled at the destination, so they are
    // never treated as missing coverage.
    if (isBuyThereItem(item))
        return false;
    const state = getItemCoverageState(item);
    return state === "uncovered" || state === "partial";
}
/**
 * Is this item flagged to be bought/handled at the destination ("לקנות שם")?
 * This is an item fulfillment/source state — NOT an assignee and NOT "everyone".
 * Buy-there items are treated as planned (not missing assignment/coverage) but
 * are never marked completed and never appear as "mine" unless the user is also
 * actually assigned.
 */
export function isBuyThereItem(item) {
    return item.fulfillmentMode === "buy_there";
}
/** Detail-sheet summary, e.g. "3/4 מכוסה · חסר 1" or "5/4 מכוסה · יותר מהנדרש". */
export function coverageSummaryText(item) {
    const { required, covered, missing, state } = getItemCoverage(item);
    const base = `${covered}/${required} מכוסה`;
    if (state === "over")
        return `${base} · יותר מהנדרש`;
    if (state === "covered")
        return base;
    return `${base} · חסר ${missing}`;
}
/** Compact row badge text — leads with what's missing, e.g. "חסר 1 · 3/4". */
export function coverageBadgeText(item) {
    const { required, covered, missing, state } = getItemCoverage(item);
    if (state === "over")
        return `${covered}/${required} · יותר מהנדרש`;
    if (state === "covered")
        return `${covered}/${required} מכוסה`;
    return `חסר ${missing} · ${covered}/${required}`;
}
/**
 * Does this item have nobody responsible for it? `all` items are considered
 * assigned (everyone). `specific` items with zero real assignees and `none`
 * items count as unassigned. Used internally; prefer {@link isItemIncompleteAssignment}
 * for the "חסר שיוך" filter.
 */
export function isItemUnassigned(item) {
    const type = getAssignmentType(item);
    if (type === "all")
        return false;
    if (type === "specific")
        return specificAssigneeCount(item) === 0;
    return true; // none
}
/**
 * Is the item's assignment incomplete? True for completely unassigned items
 * AND partially assigned items (filled slots < required). Used by the
 * "חסר שיוך" category filter. `all` items and checked items are not incomplete.
 */
export function isItemIncompleteAssignment(item) {
    // "Buy there" items are planned to be handled at the destination, so they do
    // not count as missing/incomplete assignment.
    if (isBuyThereItem(item))
        return false;
    const status = getAssignmentStatus(item);
    return status === "unassigned" || status === "partial";
}
/**
 * Returns the current user's personal completion state for an item:
 * - `none` type → the global `item.checked`
 * - `specific`/`all` → whether MY assignment row is checked (not all rows)
 *
 * This is the right value for rendering the checkbox in the UI so that
 * checking an item as one of several assignees immediately shows as done for
 * the current user, even when others haven't checked yet.
 */
export function isMyAssignmentChecked(item, myParticipantId, myDisplayName) {
    const type = getAssignmentType(item);
    if (type === "none")
        return item.checked ?? false;
    const assignments = item.assignments ?? migrateToAssignments(item);
    const mine = assignments.find((a) => !isEveryoneAssignment(a) && assignmentIsPerson(a, myParticipantId, myDisplayName));
    return mine?.checked ?? (item.checked ?? false);
}
export function isItemAssignedToMe(item, myParticipantId, myDisplayName) {
    const type = getAssignmentType(item);
    if (type === "all")
        return true;
    if (type === "specific") {
        const assignments = item.assignments ?? migrateToAssignments(item);
        return assignments.some((a) => assignmentIsPerson(a, myParticipantId, myDisplayName));
    }
    return false;
}
/**
 * Display names for an item's assignees, deduplicated. `all` renders as the
 * single everyone marker rather than every participant individually.
 */
export function resolvedAssigneeNames(item, participants) {
    const type = getAssignmentType(item);
    if (type === "all")
        return [EVERYONE_SENTINEL];
    if (type === "specific") {
        const assignments = item.assignments ?? migrateToAssignments(item);
        const names = assignments
            .filter((a) => !isEveryoneAssignment(a))
            .map((a) => a.participantId
            ? participants.find((p) => p.id === a.participantId)?.displayName ?? a.displayName
            : a.displayName);
        return dedupeStrings(names);
    }
    return [];
}
/**
 * Resolve the full assignee set for per-person summaries. Unlike
 * `resolvedAssigneeNames`, an `all` item expands to every current participant
 * so each person sees it in their breakdown exactly once.
 */
export function assigneesForItem(item, participants) {
    const type = getAssignmentType(item);
    if (type === "all") {
        const byId = new Map((item.assignments ?? []).filter((a) => !isEveryoneAssignment(a)).map((a) => [assignmentKey(a), a]));
        return participants.map((p) => {
            const existing = byId.get(`id:${p.id}`);
            return existing ?? { participantId: p.id, displayName: p.displayName };
        });
    }
    if (type === "specific") {
        const assignments = item.assignments ?? migrateToAssignments(item);
        return dedupeAssignments(assignments.filter((a) => !isEveryoneAssignment(a))).map((a) => a.participantId
            ? {
                ...a,
                displayName: participants.find((p) => p.id === a.participantId)?.displayName ?? a.displayName,
            }
            : a);
    }
    return [];
}
/** Legacy single-assign → assignments[] conversion (idempotent). */
export function migrateToAssignments(item) {
    if (item.assignments)
        return item.assignments;
    if (item.assignedTo || item.assignedToParticipantId) {
        return [
            {
                participantId: item.assignedToParticipantId,
                displayName: item.assignedTo ?? "",
            },
        ];
    }
    return [];
}
/**
 * Idempotent normalization of a single item: stamps an explicit
 * `assignmentType`, converts legacy single fields and the everyone sentinel to
 * the new model, dedupes assignment rows, and strips legacy fields. Returns the
 * same reference when the item is already normalized.
 */
export function normalizeItem(item) {
    const type = getAssignmentType(item);
    if (type === "none") {
        const alreadyClean = item.assignmentType === undefined &&
            !item.assignedTo &&
            !item.assignedToParticipantId &&
            (item.assignments === undefined || item.assignments.length === 0);
        if (alreadyClean)
            return item;
        const { assignedTo: _a, assignedToParticipantId: _b, ...rest } = item;
        return { ...rest, assignmentType: undefined, assignments: undefined };
    }
    let assignments = item.assignments ? item.assignments.slice() : migrateToAssignments(item);
    // The everyone sentinel is never a real per-person row.
    assignments = assignments.filter((a) => !isEveryoneAssignment(a));
    assignments = dedupeAssignments(assignments);
    const { assignedTo: _a, assignedToParticipantId: _b, ...rest } = item;
    return { ...rest, assignmentType: type, assignments };
}
/**
 * Normalize an entire plan's content. Returns the (possibly identical) content
 * and whether anything actually changed, so callers can avoid needless writes.
 */
export function normalizeContent(content) {
    let changed = false;
    const sections = content.sections.map((s) => ({
        ...s,
        subsections: s.subsections.map((sub) => ({
            ...sub,
            items: sub.items.map((it) => {
                const next = normalizeItem(it);
                if (next !== it && JSON.stringify(next) !== JSON.stringify(it))
                    changed = true;
                return next;
            }),
        })),
    }));
    return changed ? { content: { sections }, changed } : { content, changed: false };
}
export function resolveQuantityForParticipant(item, participantId, displayName) {
    if (item.assignments) {
        const mine = item.assignments.find((a) => assignmentIsPerson(a, participantId, displayName ?? ""));
        if (mine?.quantity)
            return mine.quantity;
    }
    return item.quantity;
}
/**
 * Legacy per-assignment toggle used for `specific` and legacy items. Prefer
 * {@link toggleMyCompletion} in UI — it also handles `all`.
 */
export function toggleAssignmentChecked(item, participantId, displayName) {
    if (!item.assignments || item.assignments.length === 0) {
        return { checked: !item.checked };
    }
    const updated = item.assignments.map((a) => {
        const isMe = assignmentIsPerson(a, participantId, displayName) || isEveryoneAssignment(a);
        return isMe ? { ...a, checked: !a.checked } : a;
    });
    const allChecked = updated.every((a) => a.checked);
    return { assignments: updated, checked: allChecked };
}
/** Are all current participants done with an `all` item? */
function allParticipantsChecked(assignments, participants) {
    if (participants.length === 0) {
        return assignments.length > 0 && assignments.every((a) => a.checked);
    }
    return participants.every((p) => assignments.some((a) => a.participantId === p.id && a.checked));
}
/**
 * Set the current user's completion for an item to an explicit `value`,
 * respecting its assignment type. This is the single source of truth for the
 * lazy-create-and-recompute logic shared by {@link toggleMyCompletion} (which
 * passes the negated current value) and {@link assignAndCompleteForMe} (which
 * forces `true`):
 * - `none`     — sets the global `checked`.
 * - `specific` — sets my assignment row; `item.checked` becomes the AND of all
 *                rows.
 * - `all`      — finds or lazily creates my row; `item.checked` becomes true
 *                only when every current participant has checked.
 */
function setMyCompletion(item, myParticipantId, myDisplayName, participants, value) {
    const type = getAssignmentType(item);
    if (type === "none") {
        return { checked: value };
    }
    if (type === "specific") {
        // Safe fallback: if my row is missing (legacy/mixed shapes or an id/name
        // mismatch) lazily create it so the checkbox fills immediately and survives
        // the next poll, instead of mapping over rows and silently changing nothing.
        const base = item.assignments ?? migrateToAssignments(item);
        const idx = base.findIndex((a) => assignmentIsPerson(a, myParticipantId, myDisplayName));
        let updated;
        if (idx >= 0) {
            updated = base.map((a, i) => (i === idx ? { ...a, checked: value } : a));
        }
        else {
            updated = [
                ...base,
                {
                    participantId: myParticipantId,
                    displayName: myDisplayName,
                    checked: value,
                    quantity: "1",
                },
            ];
        }
        const allChecked = updated.length > 0 && updated.every((a) => a.checked);
        return { assignmentType: "specific", assignments: updated, checked: allChecked };
    }
    // type === "all": lazily materialize only my own per-person row.
    const base = (item.assignments ?? []).filter((a) => !isEveryoneAssignment(a));
    const idx = base.findIndex((a) => assignmentIsPerson(a, myParticipantId, myDisplayName));
    let updated;
    if (idx >= 0) {
        updated = base.map((a, i) => (i === idx ? { ...a, checked: value } : a));
    }
    else {
        updated = [
            ...base,
            { participantId: myParticipantId, displayName: myDisplayName, checked: value },
        ];
    }
    const checked = allParticipantsChecked(updated, participants);
    return { assignmentType: "all", assignments: updated, checked };
}
/**
 * Toggle the current user's completion for an item, respecting its assignment
 * type:
 * - `none`     — toggles the global `checked` (item has no per-person state).
 * - `specific` — flips my assignment row; `item.checked` becomes the AND of all
 *                rows.
 * - `all`      — finds or lazily creates my row, flips it; `item.checked`
 *                becomes true only when every current participant has checked.
 */
export function toggleMyCompletion(item, myParticipantId, myDisplayName, participants) {
    const next = !isMyAssignmentChecked(item, myParticipantId, myDisplayName);
    return setMyCompletion(item, myParticipantId, myDisplayName, participants, next);
}
/** Build the patch that records a new assignment selection from the picker. */
export function setAssignment(type, assignments) {
    if (type === "none") {
        return {
            assignmentType: undefined,
            assignments: undefined,
            assignedTo: undefined,
            assignedToParticipantId: undefined,
        };
    }
    if (type === "all") {
        return {
            assignmentType: "all",
            assignments: dedupeAssignments(assignments.filter((a) => !isEveryoneAssignment(a))),
            assignedTo: undefined,
            assignedToParticipantId: undefined,
        };
    }
    // Stamp an explicit quantity (default 1) on every specific assignee so newly
    // assigned people contribute a unit to coverage, and edits persist quantities.
    const deduped = dedupeAssignments(assignments.filter((a) => !isEveryoneAssignment(a)));
    return {
        assignmentType: "specific",
        assignments: deduped.map((a) => ({ ...a, quantity: a.quantity ?? "1" })),
        assignedTo: undefined,
        assignedToParticipantId: undefined,
    };
}
export function addNote(item, text, author) {
    const existing = item.notes ?? [];
    return [
        ...existing,
        {
            id: genId(),
            text,
            author,
            createdAt: Math.floor(Date.now() / 1000),
        },
    ];
}
export function editNote(notes, noteId, newText) {
    return notes.map((n) => n.id !== noteId
        ? n
        : { ...n, text: newText, updatedAt: Math.floor(Date.now() / 1000) });
}
// ── Personal assignment readers ───────────────────────────────────────────────
/**
 * The current user's assignment row, id-or-name matched (the everyone sentinel
 * is never a personal row). Returns undefined when I'm not an assignee.
 */
export function getMyAssignment(item, myParticipantId, myDisplayName) {
    const assignments = item.assignments ?? migrateToAssignments(item);
    return assignments.find((a) => !isEveryoneAssignment(a) && assignmentIsPerson(a, myParticipantId, myDisplayName));
}
/** Units I'm currently responsible for (0 if I'm not assigned). */
export function getMyAssignedQuantity(item, myParticipantId, myDisplayName) {
    const mine = getMyAssignment(item, myParticipantId, myDisplayName);
    return mine ? getAssignmentQuantity(mine) : 0;
}
/**
 * Units that have actually been packed:
 * - `none`     — required if checked, else 0.
 * - `all`      — required if checked (everyone done), else 0.
 * - `specific` — sum of quantities over the CHECKED rows only.
 */
export function getItemCompletedQuantity(item) {
    const type = getAssignmentType(item);
    const required = getItemRequiredQuantity(item);
    if (type === "none")
        return item.checked ? required : 0;
    if (type === "all")
        return item.checked ? required : 0;
    const assignments = dedupeAssignments((item.assignments ?? migrateToAssignments(item)).filter((a) => !isEveryoneAssignment(a)));
    return assignments.reduce((sum, a) => (a.checked ? sum + getAssignmentQuantity(a) : sum), 0);
}
/**
 * Aggregate packing state. `complete` when packed units cover the requirement
 * (so an under-covered item never reads fully complete); `partial` when some but
 * not enough is packed; `none` when nothing is packed.
 */
export function getItemCompletionState(item) {
    const type = getAssignmentType(item);
    if (type === "all") {
        if (item.checked)
            return "complete";
        const any = (item.assignments ?? []).some((a) => !isEveryoneAssignment(a) && a.checked);
        return any ? "partial" : "none";
    }
    if (type === "none")
        return item.checked ? "complete" : "none";
    const required = getItemRequiredQuantity(item);
    const completed = getItemCompletedQuantity(item);
    if (completed <= 0)
        return "none";
    if (completed >= required)
        return "complete";
    return "partial";
}
export function isItemFullyComplete(item) {
    return getItemCompletionState(item) === "complete";
}
export function isItemPartiallyComplete(item) {
    return getItemCompletionState(item) === "partial";
}
export function getItemCompletionSummary(item) {
    return {
        completed: getItemCompletedQuantity(item),
        required: getItemRequiredQuantity(item),
        state: getItemCompletionState(item),
    };
}
/**
 * Short completion (packing) text. `specific`/`none` show a fraction "נארז X/Y";
 * `all` shows only the state word because per-unit packing is undefined for
 * everyone.
 */
export function completionSummaryText(item) {
    const type = getAssignmentType(item);
    const state = getItemCompletionState(item);
    if (type === "all") {
        if (state === "complete")
            return "נארז";
        if (state === "partial")
            return "נארז חלקית";
        return "";
    }
    const { completed, required } = getItemCompletionSummary(item);
    return `נארז ${completed}/${required}`;
}
// ── Take-responsibility mutations (never auto-complete) ───────────────────────
/**
 * Add the current participant as a SPECIFIC assignee for `quantity` units,
 * WITHOUT completing (checked stays false). Converts none→specific, appends to
 * an existing specific list, and dedupes. `item.checked` is recomputed as the
 * AND of all rows, so it can only stay/become false (my fresh row is unchecked).
 *
 * Returns an empty patch `{}` (a safe no-op when spread) if I'm already assigned
 * or the item is assigned to everyone — there is nothing to take in those cases.
 */
export function addCurrentParticipantAssignment(item, myParticipantId, myDisplayName, quantity) {
    const type = getAssignmentType(item);
    if (type === "all")
        return {};
    if (getMyAssignment(item, myParticipantId, myDisplayName))
        return {};
    const base = (item.assignments ?? migrateToAssignments(item)).filter((a) => !isEveryoneAssignment(a));
    const qty = quantity && quantity >= 1 ? Math.floor(quantity) : 1;
    const updated = dedupeAssignments([
        ...base,
        {
            participantId: myParticipantId,
            displayName: myDisplayName,
            quantity: String(qty),
            checked: false,
        },
    ]);
    const allChecked = updated.length > 0 && updated.every((a) => a.checked);
    return {
        assignmentType: "specific",
        assignments: updated,
        checked: allChecked,
        assignedTo: undefined,
        assignedToParticipantId: undefined,
    };
}
/**
 * Explicit combined action ("אני אקח ואסמן שכבר ארזתי"): add me (if needed) AND
 * mark MY row checked. Never triggered silently — only from an explicit choice.
 * If I'm already assigned (or the item is everyone's), this just completes my
 * part via the shared {@link setMyCompletion} path.
 */
export function assignAndCompleteForMe(item, myParticipantId, myDisplayName, quantity) {
    const type = getAssignmentType(item);
    if (type === "all" || getMyAssignment(item, myParticipantId, myDisplayName)) {
        return setMyCompletion(item, myParticipantId, myDisplayName, [], true);
    }
    const base = (item.assignments ?? migrateToAssignments(item)).filter((a) => !isEveryoneAssignment(a));
    const qty = quantity && quantity >= 1 ? Math.floor(quantity) : 1;
    const updated = dedupeAssignments([
        ...base,
        {
            participantId: myParticipantId,
            displayName: myDisplayName,
            quantity: String(qty),
            checked: true,
        },
    ]);
    const allChecked = updated.length > 0 && updated.every((a) => a.checked);
    return {
        assignmentType: "specific",
        assignments: updated,
        checked: allChecked,
        assignedTo: undefined,
        assignedToParticipantId: undefined,
    };
}
export function resolveCheckboxIntent(item, myParticipantId, myDisplayName) {
    if (isItemAssignedToMe(item, myParticipantId, myDisplayName))
        return { kind: "toggle" };
    // `all` always resolves to assigned-to-me above, so here the item is either
    // specific-with-other-assignees or has nobody assigned.
    if (getAssignmentType(item) === "specific" && specificAssigneeCount(item) > 0) {
        return { kind: "choice", variant: "not-mine" };
    }
    return { kind: "choice", variant: "unassigned" };
}
// ── Family checklist (רשימה לכל משפחה) ────────────────────────────────────────
//
// A `family_checklist` section is a shared template: every participant/family
// implicitly has every item, and each family marks its OWN packing progress in
// `item.familyChecklistCompletions`. This is a distinct axis from assignment and
// coverage — these items stay `assignmentType: "none"`, so they never appear as
// "חסר שיוך"/"חסר כיסוי". The aggregate `item.checked` is true only when every
// current participant has packed it, so global progress counters stay correct.
/** Normalize a raw section-kind value; unknown/missing collapses to "normal". */
export function normalizeSectionKind(kind) {
    if (kind === "shopping" || kind === "family_checklist")
        return kind;
    return "normal";
}
/** Is the given section a family checklist? */
export function isFamilyChecklistKind(kind) {
    return normalizeSectionKind(kind) === "family_checklist";
}
/** Does this completion row represent the given family/participant? */
function completionIsMine(c, participantId, displayName) {
    if (participantId !== undefined && c.participantId === participantId)
        return true;
    if (c.participantId === undefined && c.displayName === displayName)
        return true;
    return false;
}
/** Has the current participant/family packed this family-checklist item? */
export function isMyFamilyChecked(item, myParticipantId, myDisplayName) {
    const mine = (item.familyChecklistCompletions ?? []).find((c) => completionIsMine(c, myParticipantId, myDisplayName));
    return !!mine?.checked;
}
/** Are all current participants done with a family-checklist item? */
function allFamiliesChecked(completions, participants) {
    if (participants.length === 0) {
        return completions.length > 0 && completions.every((c) => c.checked);
    }
    return participants.every((p) => completions.some((c) => c.participantId === p.id && c.checked));
}
/**
 * Set the current family's completion for a family-checklist item to an explicit
 * `value`. Finds or lazily creates my row; the aggregate `item.checked` becomes
 * true only when every current participant has packed it.
 */
export function setMyFamilyChecked(item, myParticipantId, myDisplayName, participants, value) {
    const base = item.familyChecklistCompletions ?? [];
    const idx = base.findIndex((c) => completionIsMine(c, myParticipantId, myDisplayName));
    let updated;
    if (idx >= 0) {
        updated = base.map((c, i) => i === idx ? { ...c, checked: value, updatedAt: Date.now() } : c);
    }
    else {
        updated = [
            ...base,
            {
                participantId: myParticipantId,
                displayName: myDisplayName,
                checked: value,
                updatedAt: Date.now(),
            },
        ];
    }
    return {
        familyChecklistCompletions: updated,
        checked: allFamiliesChecked(updated, participants),
    };
}
/** Toggle the current family's completion for a family-checklist item. */
export function toggleMyFamilyChecked(item, myParticipantId, myDisplayName, participants) {
    const next = !isMyFamilyChecked(item, myParticipantId, myDisplayName);
    return setMyFamilyChecked(item, myParticipantId, myDisplayName, participants, next);
}
/** Quantity an item asks each family to bring/pack. Defaults to 1. */
export function getFamilyQuantity(item) {
    return getItemRequiredQuantity(item);
}
/**
 * Current family's packing progress across a set of family-checklist items
 * ("אצלנו: packed/total נארזו").
 */
export function familyChecklistSummary(items, myParticipantId, myDisplayName) {
    let packed = 0;
    for (const item of items) {
        if (isMyFamilyChecked(item, myParticipantId, myDisplayName))
            packed += 1;
    }
    return { packed, total: items.length };
}
/**
 * Per-family packing progress across a set of family-checklist items, one entry
 * per current participant ("משפחת כהן 3/6").
 */
export function perFamilyChecklistProgress(items, participants) {
    return participants.map((p) => {
        let packed = 0;
        for (const item of items) {
            const mine = (item.familyChecklistCompletions ?? []).find((c) => c.participantId === p.id);
            if (mine?.checked)
                packed += 1;
        }
        return { participantId: p.id, displayName: p.displayName, packed, total: items.length };
    });
}
// ── Shared "mine" + overview metrics (single source of truth) ───────────────
/**
 * Is this item relevant to the current participant/family? Family-checklist items
 * are implicitly relevant to every family; everything else must be assigned to me.
 * This is the same rule שלי (MyItemsTab) uses, so אצלנו can never drift from it.
 */
export function isMineItem(item, sectionKind, myParticipantId, myDisplayName) {
    if (isFamilyChecklistKind(sectionKind))
        return true;
    return isItemAssignedToMe(item, myParticipantId, myDisplayName);
}
/**
 * Has the current participant/family packed this item? Uses the family's OWN
 * completion state (never the global `item.checked`): family-checklist items use
 * the per-family row, everything else uses my assignment row.
 */
export function isMineChecked(item, sectionKind, myParticipantId, myDisplayName) {
    if (isFamilyChecklistKind(sectionKind)) {
        return isMyFamilyChecked(item, myParticipantId, myDisplayName);
    }
    return isMyAssignmentChecked(item, myParticipantId, myDisplayName);
}
/**
 * Does this item require assignment/coverage? Excludes family-checklist sections
 * (per-family packing, not coverage) and "לקנות שם" items (handled at destination).
 * Matches the predicate behind the "חסר שיוך / חסר כיסוי" collection.
 */
export function isAssignableItem(item, sectionKind) {
    return !isFamilyChecklistKind(sectionKind) && !isBuyThereItem(item);
}
/**
 * Compute the three Overview hero metrics from a single set of predicates so that
 * Overview, שלי, and אוספים can never contradict each other.
 *
 * - mine ("אצלנו"): item-count over items relevant to the current family, packed
 *   via the family's OWN completion state.
 * - global ("כל התכנון"): item-count over all items; an item counts as packed when
 *   `item.checked` (per-item global completion). One item = one packing task.
 * - assignment ("שיוך"): item-count over assignable items; an item is "assigned"
 *   only when fully covered. `missing` equals the "missing" collection count.
 */
export function computeOverviewMetrics(content, myParticipantId, myDisplayName) {
    let mineTotal = 0;
    let minePacked = 0;
    let globalTotal = 0;
    let globalPacked = 0;
    let assignTotal = 0;
    let assignMissing = 0;
    const seenMine = new Set();
    for (const section of content.sections) {
        const kind = section.sectionKind;
        for (const sub of section.subsections) {
            for (const item of sub.items) {
                // כל התכנון — every item is one packing task.
                globalTotal += 1;
                if (item.checked)
                    globalPacked += 1;
                // אצלנו — relevant to my family, packed via my family's own state.
                if (isMineItem(item, kind, myParticipantId, myDisplayName) && !seenMine.has(item.id)) {
                    seenMine.add(item.id);
                    mineTotal += 1;
                    if (isMineChecked(item, kind, myParticipantId, myDisplayName))
                        minePacked += 1;
                }
                // שיוך — only items that require coverage.
                if (isAssignableItem(item, kind)) {
                    assignTotal += 1;
                    if (isItemMissingCoverage(item))
                        assignMissing += 1;
                }
            }
        }
    }
    return {
        mine: { packed: minePacked, total: mineTotal, remaining: Math.max(0, mineTotal - minePacked) },
        global: { packed: globalPacked, total: globalTotal },
        assignment: {
            assigned: assignTotal - assignMissing,
            total: assignTotal,
            missing: assignMissing,
        },
    };
}
